import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { SparkleIcon, X, ArrowRight, Eye } from "@phosphor-icons/react";

/**
 * One-time onboarding tile for Tier 1 / Tier 2 users.
 * Dismisses to localStorage (`span_welcome_dismissed_<user_id>`).
 * Auto-hides once the user has filed >=3 sightings OR explicitly dismissed.
 *
 * Props:
 *   taskCount     — number to display in "You have N tasks today"
 *   sightingCount — total sightings filed by this user (≥3 → tile self-hides)
 *   variant       — "ops" (T1 field operator) | "manager" (T2 case manager)
 */
export default function WelcomeQuickStart({ taskCount = 0, sightingCount = 0, variant = "ops" }) {
  const { user } = useAuth();
  const lsKey = `span_welcome_dismissed_${user?.user_id || "anon"}`;
  const [dismissed, setDismissed] = useState(true); // default true → no flash on mount
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try { setDismissed(localStorage.getItem(lsKey) === "1"); }
    catch { setDismissed(false); }
    setHydrated(true);
  }, [lsKey]);

  // Auto-hide once the user is established (≥3 sightings filed)
  if (!hydrated) return null;
  if (dismissed) return null;
  if (sightingCount >= 3) return null;

  const close = () => {
    try { localStorage.setItem(lsKey, "1"); } catch { /* noop */ }
    setDismissed(true);
  };

  const firstName = user?.name?.split(" ")[0] || user?.initials || "there";
  const isManager = variant === "manager";

  return (
    <div
      className="card-flat p-5 sm:p-6 relative overflow-hidden"
      data-testid="welcome-quick-start"
      style={{
        background: "linear-gradient(135deg, rgba(245, 90, 30, 0.06) 0%, rgba(0, 78, 235, 0.04) 100%)",
        borderLeft: "3px solid var(--span-accent, #F55A1E)",
      }}
    >
      <button
        onClick={close}
        data-testid="welcome-dismiss"
        aria-label="Dismiss welcome tile"
        className="absolute top-3 right-3 w-7 h-7 inline-flex items-center justify-center rounded text-[var(--span-text-2)] hover:bg-[var(--span-surface-3)] transition-colors"
      >
        <X size={14} />
      </button>

      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 bg-[var(--span-accent, #F55A1E)] flex items-center justify-center shrink-0 rounded">
          <SparkleIcon size={18} weight="fill" color="white" />
        </div>
        <div>
          <div className="label-eyebrow text-[var(--span-accent, #F55A1E)]">Welcome to SPAN CASEPRIME</div>
          <h2 className="font-display font-bold text-[20px] sm:text-[22px] leading-tight mt-1">
            Quick-Start, {firstName}.
          </h2>
        </div>
      </div>

      <div className="text-[13px] text-[var(--span-text-2)] leading-relaxed mb-4 max-w-[520px]">
        {taskCount > 0 ? (
          <>You have <strong className="text-[var(--span-text-1)]" data-testid="welcome-task-count">{taskCount}</strong>{" "}
          {taskCount === 1 ? "task" : "tasks"} on your plate today.{" "}
          {isManager ? "Your team is depending on you — let\u2019s get rolling." : "Let\u2019s knock the first one out."}</>
        ) : (
          <>No tasks waiting on you right now — that means you{"\u2019"}re free to <strong className="text-[var(--span-text-1)]">file the first sighting</strong> from the field. Even a single photo + brand name kicks off the entire SPAN workflow.</>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2.5">
        <Link
          to="/reports/sighting/new"
          data-testid="welcome-cta-sighting"
          className="span-btn h-10 text-[12px] inline-flex items-center gap-2"
          style={{ background: "var(--span-accent, #F55A1E)", borderColor: "var(--span-accent, #F55A1E)" }}
        >
          <Eye size={14} weight="fill" /> File your first sighting <ArrowRight size={13} />
        </Link>
        {isManager && (
          <Link
            to="/cases"
            data-testid="welcome-cta-cases"
            className="span-btn span-btn-ghost h-10 text-[12px]"
          >
            Open the case board
          </Link>
        )}
        <button
          onClick={close}
          data-testid="welcome-skip"
          className="text-[11px] text-[var(--span-text-2)] hover:text-[var(--span-text-1)] underline ml-1"
        >
          I{"\u2019"}ll explore on my own
        </button>
      </div>
    </div>
  );
}
